import { useRef, type ComponentType, type ReactNode } from 'react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuClose,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { ListTablePagination } from '@eureca/ui';
import { ProgramConfirmDialog } from '../programs-list/ProgramConfirmDialog';
import { RadioIcon, FilePenIcon, FlagIcon, BanIcon, SearchIcon } from '../programs-list/icons';
import {
  ACTION_TOOLTIP,
  PHASE_STATUS_LABEL,
  SUBSCRIPTION_STATUS_LABEL,
  actionsForStatus,
  formatCivilDateBr,
  type CandidatePhaseStatus,
  type CandidateSubscriptionStatus,
  type PhaseTab,
  type ProgramManagementAction,
  type ProgramStatusKey,
} from './program-management-model';
import type { ProgramCandidateRow } from './program-candidates-query';
import { CandidateFilterDrawer, type CandidateFilterDrawerProps } from './CandidateFilterDrawer';
import { CandidateSearchDialog } from './CandidateSearchDialog';
import {
  ArrowLeftIcon,
  ArrowUpRightIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CopyIcon,
  DownloadIcon,
  FilterIcon,
  HandshakeIcon,
  BriefcaseBusinessIcon,
  LockIcon,
  LockOpenIcon,
  LogOutIcon,
  RocketIcon,
  SquareCheckIcon,
  SquareXIcon,
  TriangleAlertIcon,
  UserIcon,
  XIcon,
} from './icons';

/**
 * EU-593 — tela "Gerenciar programa" (frames Management, 31 no Figma).
 * Quatro blocos: card do programa (ações por estado), 3 cards de ação,
 * abas por fase + busca/filtro, tabela de candidatos com resumo rápido.
 */

export type ProgramManagementView = 'loading' | 'error' | 'empty-program' | 'empty-search' | 'rows';

export type ProgramManagementDialog = 'end' | 'cancel' | null;

// EU-654 - o dropdown do card passa a ofertar os tipos DINAMICOS do backend
// (RN-02/03): Completa sempre + testes com etapa. 'form' morreu no menu.
export type ExportResponsesType =
  | 'complete'
  // DEV-498 — exportação em massa (sempre ofertadas pelo backend — AC-02/03).
  | 'bulk-resumes'
  | 'bulk-attestations'
  | 'fit-cultural'
  | 'learning-map'
  | 'career-xray';

export interface ProgramManagementPageProps {
  programName: string;
  statusKey: ProgramStatusKey;
  deadline: { start: string; end: string } | null;
  companyLogoUrl?: string | null;
  companyName?: string | null;
  /** Programa sem NENHUMA inscrição — esconde abas/tabela e desabilita exports (GPR-004/005). */
  hasCandidates: boolean;
  /**
   * EU-593 — reflexo do BFF (`/me/permissions.canExportRankSubscribers`, role
   * `rank-consultor` SEM bypass de admin): sem isso o "Exportar inscritos" fica
   * desabilitado. A autoridade é o rank; o SPA só espelha.
   */
  canExportSubscribers: boolean;
  isActionInFlight?: boolean;
  onAction: (action: ProgramManagementAction) => void;
  onBack: () => void;
  activeDialog: ProgramManagementDialog;
  onDialogOpenChange: (open: boolean) => void;
  onConfirmEnd: () => void;
  onConfirmCancel: () => void;
  onExportSubscribers: () => void;
  onExportResponses: (type: ExportResponsesType) => void;
  /**
   * DEV-269 — card "Alinhar vagas" → "Editar formulário" (abre o construtor do
   * formulário de alinhamento). As outras duas opções do menu (compartilhar,
   * exportar) são de DEV-268. Disponível em qualquer status (DEV-268 AC-04).
   */
  onEditAlignmentForm: () => void;
  /** DEV-268 — "Compartilhar formulário" (abre o diálogo) e "Exportar alinhamento". */
  onShareAlignmentForm: () => void;
  onExportAlignment: () => void;
  /**
   * EU-654 RN-02/03 — tipos ofertáveis no dropdown "Tipo de base:" (vêm do
   * backend); `undefined` = disponibilidade carregando.
   */
  exportResponseTypes: ExportResponsesType[] | undefined;
  tabs: PhaseTab[];
  activeTab: string | null;
  onTabChange: (stepTypeKey: string | null) => void;
  /** Busca OU filtro ativos — contadores exibem asterisco (GPR-034/049). */
  countsFiltered: boolean;
  appliedSearch: string | null;
  onOpenSearch: () => void;
  onClearSearch: () => void;
  searchOpen: boolean;
  onSearchOpenChange: (open: boolean) => void;
  onSearchSubmit: (term: string) => void;
  filterActive: boolean;
  onOpenFilter: () => void;
  filterDrawer: CandidateFilterDrawerProps;
  view: ProgramManagementView;
  rows: ProgramCandidateRow[];
  expandedSubscriptionId: string | null;
  onToggleRow: (subscriptionId: string) => void;
  onCopyEmail: (row: ProgramCandidateRow) => void;
  onCopyPhone: (row: ProgramCandidateRow) => void;
  onOpenResume: (row: ProgramCandidateRow) => void;
  onOpenReport: (row: ProgramCandidateRow) => void;
  onOpenFullProfile: (row: ProgramCandidateRow) => void;
  page: number;
  totalPages: number;
  pageSize: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRetry: () => void;
}

export function ProgramManagementPage(props: ProgramManagementPageProps) {
  const showList = props.hasCandidates;
  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="programs" />

      <main className="flex flex-1 flex-col items-center bg-card px-4 pt-8 pb-16 md:px-8">
        <div className="flex w-full max-w-[1280px] flex-col gap-6">
          <button
            type="button"
            data-testid="program-management-back"
            onClick={props.onBack}
            className="flex w-fit flex-row items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            <ArrowLeftIcon className="size-3.5" />
            Voltar
          </button>

          <header className="flex flex-row items-center gap-3">
            {props.companyLogoUrl ? (
              <img
                src={props.companyLogoUrl}
                alt={props.companyName ?? ''}
                className="size-9 rounded-md object-contain"
              />
            ) : null}
            <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
              Gerenciar programa
            </h1>
          </header>

          {/* Blocos 1 + 2 — card do programa + cards de ação. */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,520px)_repeat(3,minmax(0,1fr))]">
            <section className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-xs">
              <div>
                <ProgramStatusBadge statusKey={props.statusKey} />
              </div>
              <h2 className="text-2xl font-medium leading-tight tracking-tight text-foreground">
                {props.programName}
              </h2>
              {props.deadline ? (
                <p
                  data-testid="program-management-deadline"
                  className="text-sm text-muted-foreground"
                >
                  Prazo de inscrição:{' '}
                  <span className="font-medium text-foreground">{props.deadline.start}</span> até{' '}
                  <span className="font-medium text-foreground">{props.deadline.end}</span>
                </p>
              ) : null}
              <ProgramActionsRow
                statusKey={props.statusKey}
                disabled={props.isActionInFlight ?? false}
                onAction={props.onAction}
              />
            </section>

            <ActionCard
              testId="program-management-export-subscribers"
              label="Exportar inscritos"
              Icon={DownloadIcon}
              enabled={props.hasCandidates && props.canExportSubscribers}
              disabledTooltip={
                props.canExportSubscribers ? 'Sem candidatos' : 'Você não tem acesso ao rank'
              }
              onClick={props.onExportSubscribers}
            />
            <ExportResponsesCard
              enabled={props.hasCandidates}
              onExport={props.onExportResponses}
              types={props.exportResponseTypes}
            />
            <AlignJobsCard
              onShareForm={props.onShareAlignmentForm}
              onEditForm={props.onEditAlignmentForm}
              onExport={props.onExportAlignment}
            />
          </div>

          {/* Blocos 3 + 4 — abas + tabela (só com candidatos, GPR-004). */}
          {showList ? (
            <>
              <div className="flex flex-row items-end justify-between gap-4 border-b border-border">
                <nav role="tablist" className="flex flex-row gap-2 overflow-x-auto">
                  {props.tabs.map((tab) => {
                    const selected = (tab.stepTypeKey ?? null) === (props.activeTab ?? null);
                    return (
                      <button
                        key={tab.stepTypeKey ?? 'all'}
                        role="tab"
                        aria-selected={selected}
                        data-testid={`program-candidates-tab-${tab.stepTypeKey ?? 'all'}`}
                        onClick={() => props.onTabChange(tab.stepTypeKey)}
                        className={cn(
                          'whitespace-nowrap border-b-2 px-4 pb-3 pt-1 text-sm transition-colors',
                          selected
                            ? 'border-primary font-medium text-foreground'
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                        )}
                      >
                        {tab.label} · {tab.count}
                        {props.countsFiltered ? '*' : ''}
                      </button>
                    );
                  })}
                </nav>
                <div className="flex flex-row items-center gap-2 pb-2">
                  <button
                    type="button"
                    aria-label="Buscar candidatos"
                    data-testid="program-candidates-open-search"
                    onClick={props.onOpenSearch}
                    className="flex size-9 items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <SearchIcon className="size-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Filtrar candidatos"
                    data-testid="program-candidates-open-filter"
                    onClick={props.onOpenFilter}
                    className="relative flex size-9 items-center justify-center rounded-md border border-input bg-card text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <FilterIcon className="size-4" />
                    {props.filterActive ? (
                      <span
                        data-testid="program-candidates-filter-indicator"
                        className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-destructive"
                      />
                    ) : null}
                  </button>
                </div>
              </div>

              {props.appliedSearch ? (
                <div className="flex flex-row items-center justify-between">
                  <p className="text-sm text-foreground">
                    Resultados para: <span className="font-medium">{props.appliedSearch}</span>
                  </p>
                  <button
                    type="button"
                    data-testid="program-candidates-clear-search"
                    onClick={props.onClearSearch}
                    className="text-sm font-medium text-primary underline-offset-2 hover:underline"
                  >
                    Limpar busca
                  </button>
                </div>
              ) : null}

              <CandidatesTableBlock {...props} />
            </>
          ) : (
            <EmptyState
              title="Sem candidatos"
              description="Esse programa ainda não recebeu nenhuma inscrição."
            />
          )}
        </div>
      </main>

      {/* Modais encerrar/cancelar (GPR-027/029 — textos literais do Figma). */}
      <ProgramConfirmDialog
        open={props.activeDialog === 'end'}
        onOpenChange={props.onDialogOpenChange}
        title="Deseja encerrar esse programa?"
        description="Ele deixará de receber novas candidaturas e não poderá ser reaberto."
        confirmLabel={props.isActionInFlight ? 'Encerrando...' : 'Confirmar encerramento'}
        onConfirm={props.onConfirmEnd}
      />
      <ProgramConfirmDialog
        open={props.activeDialog === 'cancel'}
        onOpenChange={props.onDialogOpenChange}
        title="Deseja cancelar essa programa?"
        description="Ao cancelar, esse programa deixa de receber novas candidaturas, não pode ser reaberto e nenhuma movimentação será possível nas vagas dele."
        confirmLabel={props.isActionInFlight ? 'Cancelando...' : 'Confirmar cancelamento'}
        onConfirm={props.onConfirmCancel}
      />

      <CandidateSearchDialog
        open={props.searchOpen}
        onOpenChange={props.onSearchOpenChange}
        currentTerm={props.appliedSearch ?? ''}
        onSearch={props.onSearchSubmit}
      />
      <CandidateFilterDrawer {...props.filterDrawer} />
    </div>
  );
}

// ── Tabela ────────────────────────────────────────────────────────────

function CandidatesTableBlock(props: ProgramManagementPageProps) {
  if (props.view === 'loading') {
    return (
      <div
        data-testid="program-candidates-loading"
        className="flex flex-col gap-3 rounded-xl border border-border p-6"
      >
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-14 animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    );
  }
  if (props.view === 'error') {
    return (
      <div
        data-testid="program-candidates-error"
        className="flex flex-col items-center gap-4 rounded-xl border border-border p-12 text-center"
      >
        <span className="flex size-14 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <TriangleAlertIcon className="size-6" />
        </span>
        <div className="flex flex-col gap-1">
          <p className="text-xl font-medium text-foreground">
            Não foi possível carregar os candidatos
          </p>
          <p className="text-sm text-muted-foreground">
            Algo deu errado ao falar com o servidor. Tente novamente.
          </p>
        </div>
        <Button variant="outline" onClick={props.onRetry} data-testid="program-candidates-retry">
          Tentar novamente
        </Button>
      </div>
    );
  }
  if (props.view === 'empty-search') {
    return (
      <EmptyState
        title="Não achamos nada"
        description="Nenhum candidato corresponde à busca ou aos filtros aplicados. Tente ajustar o recorte."
        testId="program-candidates-empty-search"
      />
    );
  }
  if (props.view === 'empty-program') {
    return (
      <EmptyState
        title="Sem candidatos"
        description="Esse programa ainda não recebeu nenhuma inscrição."
        testId="program-candidates-empty"
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[960px] border-collapse text-left">
          <thead>
            <tr className="border-b border-border text-sm text-foreground">
              <th className="px-4 py-4 font-medium">Candidato</th>
              <th className="px-4 py-4 font-medium">Vaga</th>
              <th className="px-4 py-4 font-medium">Status inscrição</th>
              <th className="px-4 py-4 font-medium">Fase</th>
              <th className="px-4 py-4 font-medium">Qualificado(a)</th>
              <th className="px-4 py-4 font-medium">Aptidão</th>
              <th className="w-12 px-4 py-4" />
            </tr>
          </thead>
          <tbody>
            {props.rows.map((row) => (
              <CandidateRow
                key={row.subscriptionId}
                row={row}
                expanded={props.expandedSubscriptionId === row.subscriptionId}
                onToggle={() => props.onToggleRow(row.subscriptionId)}
                onCopyEmail={() => props.onCopyEmail(row)}
                onCopyPhone={() => props.onCopyPhone(row)}
                onOpenResume={() => props.onOpenResume(row)}
                onOpenReport={() => props.onOpenReport(row)}
                onOpenFullProfile={() => props.onOpenFullProfile(row)}
              />
            ))}
          </tbody>
        </table>
      </div>
      <ListTablePagination
        page={props.page}
        totalPages={props.totalPages}
        rowsPerPage={props.pageSize}
        rowsPerPageOptions={props.pageSizeOptions}
        onPageChange={props.onPageChange}
        onRowsPerPageChange={props.onPageSizeChange}
        data-testid="program-candidates-pagination"
      />
    </div>
  );
}

function CandidateRow({
  row,
  expanded,
  onToggle,
  onCopyEmail,
  onCopyPhone,
  onOpenResume,
  onOpenReport,
  onOpenFullProfile,
}: {
  row: ProgramCandidateRow;
  expanded: boolean;
  onToggle: () => void;
  onCopyEmail: () => void;
  onCopyPhone: () => void;
  onOpenResume: () => void;
  onOpenReport: () => void;
  onOpenFullProfile: () => void;
}) {
  return (
    <>
      <tr
        data-testid={`program-candidate-row-${row.subscriptionId}`}
        onClick={onToggle}
        className="cursor-pointer border-b border-border text-sm transition-colors hover:bg-muted/30"
      >
        <td className="px-4 py-3">
          <div className="flex flex-row items-center gap-3">
            <Avatar>
              {row.avatarUrl ? <AvatarImage src={row.avatarUrl} alt="" /> : null}
              <AvatarFallback>
                <UserIcon className="size-4 text-primary" />
              </AvatarFallback>
            </Avatar>
            <div className="flex min-w-0 flex-col">
              {/* GPR-013 — nome social; o nome civil nunca aparece aqui. */}
              <span className="truncate font-medium text-foreground">{row.name ?? '—'}</span>
              <span className="truncate text-muted-foreground">{row.email ?? '—'}</span>
            </div>
          </div>
        </td>
        <td className="max-w-[240px] truncate px-4 py-3 text-foreground">{row.jobName}</td>
        <td className="px-4 py-3">
          <SubscriptionStatusBadge status={row.subscriptionStatus} />
        </td>
        <td className="px-4 py-3">
          {row.phase ? (
            <div className="flex flex-col">
              <span className="font-medium text-foreground">{row.phase.stepName ?? '—'}</span>
              <PhaseStatusLabel status={row.phase.status} />
            </div>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-4 py-3">
          <QualifiedIcon qualified={row.qualified} />
        </td>
        <td className="px-4 py-3">
          <AptitudeBadge apt={row.apt} />
        </td>
        <td className="px-4 py-3">
          <span className="text-muted-foreground" aria-hidden>
            {expanded ? (
              <ChevronUpIcon className="size-4" />
            ) : (
              <ChevronDownIcon className="size-4" />
            )}
          </span>
        </td>
      </tr>
      {expanded ? (
        <tr className="border-b border-border bg-muted/20">
          <td colSpan={7} className="px-4 py-5">
            <CandidatePreviewPanel
              row={row}
              onCopyEmail={onCopyEmail}
              onCopyPhone={onCopyPhone}
              onOpenResume={onOpenResume}
              onOpenReport={onOpenReport}
              onOpenFullProfile={onOpenFullProfile}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** Resumo rápido (frame Management / Profile preview — GPR-053..060). */
function CandidatePreviewPanel({
  row,
  onCopyEmail,
  onCopyPhone,
  onOpenResume,
  onOpenReport,
  onOpenFullProfile,
}: {
  row: ProgramCandidateRow;
  onCopyEmail: () => void;
  onCopyPhone: () => void;
  onOpenResume: () => void;
  onOpenReport: () => void;
  onOpenFullProfile: () => void;
}) {
  const p = row.preview;
  const cityLine =
    p.cityName && p.stateAcronym ? `${p.cityName} - ${p.stateAcronym}` : (p.cityName ?? null);
  const subscribedAtBr = formatCivilDateBr(row.subscribedAt.slice(0, 10));
  return (
    <div
      data-testid={`program-candidate-preview-${row.subscriptionId}`}
      className="flex flex-col gap-4"
    >
      <p className="text-base font-medium text-foreground">Resumo do perfil</p>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-5">
        <PreviewSection title="Dados pessoais">
          <PreviewLine value={row.userId} />
          <PreviewLine value={p.phone} />
          <PreviewLine value={p.document} />
          <PreviewLine value={formatCivilDateBr(p.birthDate)} />
          <PreviewLine value={cityLine} />
        </PreviewSection>
        <PreviewSection title="Inscrição">
          <PreviewLine value={row.subscriptionId} />
          <PreviewLine value={subscribedAtBr ? `Feita ${subscribedAtBr}` : null} />
          <PreviewLine value={p.needsAccessibility === true ? 'Precisa de acessibilidade' : null} />
          <PreviewLine
            value={p.workAreaTitle ? `Deseja atuar na área de ${p.workAreaTitle}` : null}
          />
          <PreviewLine
            value={p.workAreaCities.length > 0 ? `Deseja atuar em ${p.workAreaCities[0]}` : null}
          />
          <PreviewLine value={p.acceptRelocation === true ? 'Aceita ser realocado' : null} />
        </PreviewSection>
        {/* GPR-055/056 — UMA formação: compatível cadastrada primeiro, senão a
            primeira cadastrada (decidido no backend). */}
        <PreviewSection title="Formação">
          {p.formation ? (
            <>
              <PreviewLine value={p.formation.courseName} />
              <PreviewLine value={p.formation.institutionName} />
              <PreviewLine value={formatCivilDateBr(p.formation.formationDate)} />
              <PreviewLine value={p.formation.courseTypeName} />
            </>
          ) : (
            <PreviewLine value="Sem formação cadastrada" muted />
          )}
        </PreviewSection>
        <PreviewSection title="Diversidade">
          <PreviewLine value={p.genderName} />
          <PreviewLine value={p.ethnicityName} />
          <PreviewLine value={p.sexualOrientationName} />
          <PreviewLine value={p.isPwdName === 'Sim' ? 'É pessoa com deficiência' : null} />
          <PreviewLine value={p.familyIncomeName ? `Renda familiar ${p.familyIncomeName}` : null} />
        </PreviewSection>
        <PreviewSection title="Ações">
          {p.resumeKey ? (
            <PreviewAction
              label="Acessar currículo"
              testId={`preview-resume-${row.subscriptionId}`}
              onClick={onOpenResume}
            />
          ) : null}
          {/* GPR-057 — sem laudo, a ação não renderiza. */}
          {p.attestationKey ? (
            <PreviewAction
              label="Acessar laudo"
              testId={`preview-report-${row.subscriptionId}`}
              onClick={onOpenReport}
            />
          ) : null}
          <PreviewAction
            label="Copiar e-mail"
            testId={`preview-copy-email-${row.subscriptionId}`}
            onClick={onCopyEmail}
          />
          <PreviewAction
            label="Copiar telefone"
            testId={`preview-copy-phone-${row.subscriptionId}`}
            onClick={onCopyPhone}
          />
        </PreviewSection>
      </div>
      <div className="flex justify-center pt-2">
        <button
          type="button"
          data-testid={`preview-full-profile-${row.subscriptionId}`}
          onClick={onOpenFullProfile}
          className="flex flex-row items-center gap-1 text-sm font-medium text-primary underline-offset-2 hover:underline"
        >
          Acessar perfil completo
          <ArrowUpRightIcon className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

function PreviewSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-sm font-medium text-foreground">{title}</p>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );
}

function PreviewLine({ value, muted }: { value: string | null; muted?: boolean }) {
  if (!value) return null;
  return (
    <p
      className={cn(
        'break-words text-xs leading-relaxed',
        muted ? 'text-muted-foreground' : 'text-muted-foreground'
      )}
    >
      {value}
    </p>
  );
}

function PreviewAction({
  label,
  onClick,
  testId,
}: {
  label: string;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="w-fit text-xs font-medium text-primary underline underline-offset-2 hover:text-primary/80"
    >
      {label}
    </button>
  );
}

// ── Badges e estados ─────────────────────────────────────────────────

function ProgramStatusBadge({ statusKey }: { statusKey: ProgramStatusKey }): ReactNode {
  switch (statusKey) {
    case 'published':
      return (
        <Badge className="bg-green-300 text-green-950" leftIcon={<RadioIcon className="size-3" />}>
          Ativa
        </Badge>
      );
    case 'private':
      return (
        <Badge className="bg-green-300 text-green-950" leftIcon={<RadioIcon className="size-3" />}>
          Ativa e priv.
        </Badge>
      );
    case 'not_published':
      return (
        <Badge
          className="bg-warning text-primary-foreground"
          leftIcon={<FilePenIcon className="size-3" />}
        >
          Rascunho
        </Badge>
      );
    case 'expired':
    case 'inactivated':
      return (
        <Badge className="bg-muted text-foreground" leftIcon={<FlagIcon className="size-3" />}>
          Encerrada
        </Badge>
      );
    case 'cancelled':
      return (
        <Badge
          className="bg-destructive/15 text-destructive"
          leftIcon={<BanIcon className="size-3" />}
        >
          Cancelado
        </Badge>
      );
  }
}

function SubscriptionStatusBadge({ status }: { status: CandidateSubscriptionStatus }) {
  const label = SUBSCRIPTION_STATUS_LABEL[status];
  switch (status) {
    case 'active':
      return (
        <Badge className="bg-blue-200 text-blue-950" leftIcon={<RadioIcon className="size-3" />}>
          {label}
        </Badge>
      );
    case 'hired':
      return (
        <Badge
          className="bg-green-300 text-green-950"
          leftIcon={<HandshakeIcon className="size-3" />}
        >
          {label}
        </Badge>
      );
    case 'rejected':
      return (
        <Badge className="bg-destructive text-white" leftIcon={<BanIcon className="size-3" />}>
          {label}
        </Badge>
      );
    case 'withdrawn':
      return (
        <Badge
          className="bg-orange-200 text-orange-950"
          leftIcon={<LogOutIcon className="size-3" />}
        >
          {label}
        </Badge>
      );
    case 'closed':
      return (
        <Badge className="bg-muted text-foreground" leftIcon={<FlagIcon className="size-3" />}>
          {label}
        </Badge>
      );
    case 'cancelled':
      return (
        <Badge
          className="bg-destructive/15 text-destructive"
          leftIcon={<BanIcon className="size-3" />}
        >
          {label}
        </Badge>
      );
  }
}

function PhaseStatusLabel({ status }: { status: CandidatePhaseStatus }) {
  return (
    <span
      className={cn(
        'text-xs',
        status === 'completed' ? 'font-medium text-primary' : 'text-muted-foreground'
      )}
    >
      {PHASE_STATUS_LABEL[status]}
    </span>
  );
}

function QualifiedIcon({ qualified }: { qualified: boolean }) {
  return qualified ? (
    <span
      aria-label="Qualificado"
      className="flex size-6 items-center justify-center rounded-full bg-green-300 text-green-950"
    >
      <CheckIcon className="size-3.5" />
    </span>
  ) : (
    <span
      aria-label="Não qualificado"
      className="flex size-6 items-center justify-center rounded-full bg-muted text-muted-foreground"
    >
      <XIcon className="size-3.5" />
    </span>
  );
}

function AptitudeBadge({ apt }: { apt: boolean }) {
  return apt ? (
    <Badge
      className="bg-green-300 text-green-950"
      leftIcon={<SquareCheckIcon className="size-3" />}
    >
      Apto
    </Badge>
  ) : (
    <Badge className="bg-destructive text-white" leftIcon={<SquareXIcon className="size-3" />}>
      Inapto
    </Badge>
  );
}

function EmptyState({
  title,
  description,
  testId,
}: {
  title: string;
  description: string;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId ?? 'program-management-empty'}
      className="flex flex-col items-center gap-4 py-16 text-center"
    >
      <span className="flex size-16 items-center justify-center rounded-full bg-muted text-foreground">
        <TriangleAlertIcon className="size-7" />
      </span>
      <div className="flex flex-col gap-1">
        <p className="text-2xl font-medium text-foreground">{title}</p>
        <p className="text-base text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

// ── Card do programa: linha de ações por status ───────────────────────

function ProgramActionsRow({
  statusKey,
  disabled,
  onAction,
}: {
  statusKey: ProgramStatusKey;
  disabled: boolean;
  onAction: (action: ProgramManagementAction) => void;
}) {
  const actions = actionsForStatus(statusKey);
  const ICONS: Record<string, ComponentType<{ className?: string }>> = {
    publish: RocketIcon,
    'make-private': LockIcon,
    'make-public': LockOpenIcon,
    duplicate: CopyIcon,
    end: FlagIcon,
    cancel: BanIcon,
  };
  return (
    <div className="flex flex-row items-center gap-2 pt-1">
      <Button
        data-testid="program-management-primary-action"
        onClick={() => onAction(actions.primary)}
        disabled={disabled}
      >
        {actions.primary === 'edit' ? 'Editar informações' : 'Conferir informações'}
      </Button>
      <TooltipProvider>
        {actions.icons.map((action) => {
          const Icon = ICONS[action] ?? CopyIcon;
          return (
            <Tooltip key={action}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  data-testid={`program-management-action-${action}`}
                  aria-label={ACTION_TOOLTIP[action]}
                  onClick={() => onAction(action)}
                  disabled={disabled}
                  className={cn(
                    'inline-flex size-9 items-center justify-center rounded-md border border-input bg-card text-foreground',
                    'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                    'disabled:pointer-events-none disabled:opacity-50'
                  )}
                >
                  <Icon className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{ACTION_TOOLTIP[action]}</TooltipContent>
            </Tooltip>
          );
        })}
      </TooltipProvider>
    </div>
  );
}

// ── Cards de ação ─────────────────────────────────────────────────────

const ACTION_CARD_CLASS =
  'flex h-full min-h-[176px] w-full flex-col items-start justify-between gap-10 rounded-xl border border-border bg-card p-5 text-left shadow-xs';

function ActionCardBody({
  label,
  Icon,
  enabled,
}: {
  label: string;
  Icon: ComponentType<{ className?: string }>;
  enabled: boolean;
}) {
  return (
    <>
      <span
        className={cn(
          'flex size-9 items-center justify-center rounded-md bg-secondary text-primary',
          !enabled && 'opacity-50'
        )}
      >
        <Icon className="size-4" />
      </span>
      <span
        className={cn(
          'text-base font-medium leading-none',
          enabled ? 'text-accent-foreground' : 'text-muted-foreground/70'
        )}
      >
        {label}
      </span>
    </>
  );
}

/** Radix Tooltip não dispara em `disabled` — wrapper focável (padrão EU-560). */
function ActionCard({
  testId,
  label,
  Icon,
  enabled,
  disabledTooltip,
  onClick,
}: {
  testId: string;
  label: string;
  Icon: ComponentType<{ className?: string }>;
  enabled: boolean;
  disabledTooltip: string;
  onClick: () => void;
}) {
  if (enabled) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className={cn(
          ACTION_CARD_CLASS,
          'transition-colors hover:border-primary/40 hover:bg-muted/40',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
        )}
      >
        <ActionCardBody label={label} Icon={Icon} enabled />
      </button>
    );
  }
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            data-testid={`${testId}-disabled-wrapper`}
            // Bloco com a altura da linha do grid: com o nome do programa em duas
            // linhas os quatro cards do cabeçalho seguem com a mesma altura.
            className="block h-full cursor-not-allowed"
          >
            <button type="button" data-testid={testId} disabled className={ACTION_CARD_CLASS}>
              <ActionCardBody label={label} Icon={Icon} enabled={false} />
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{disabledTooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * DEV-269 + DEV-268 — card "Alinhar vagas" (4ª ação do cabeçalho; Figma
 * 16077:30423 / 16077:31038, ícone lucide `briefcase-business`). Menu com
 * exatamente três itens, nesta ordem (DEV-268 AC-02): "Compartilhar
 * formulário", "Editar formulário", "Exportar alinhamento". Nunca desabilitado
 * pelo status do programa (AC-04). `Esc`, clique fora e escolha fecham o menu e
 * devolvem o foco ao card (Radix, AC-03).
 */
function AlignJobsCard({
  onShareForm,
  onEditForm,
  onExport,
}: {
  onShareForm: () => void;
  onEditForm: () => void;
  onExport: () => void;
}) {
  const cardRef = useRef<HTMLButtonElement>(null);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          ref={cardRef}
          type="button"
          data-testid="program-management-align-jobs"
          className={cn(
            ACTION_CARD_CLASS,
            'transition-colors hover:border-primary/40 hover:bg-muted/40',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
          )}
        >
          <ActionCardBody label="Alinhar vagas" Icon={BriefcaseBusinessIcon} enabled />
        </button>
      </DropdownMenuTrigger>
      {/* Figma 16077:31045: menu logo abaixo do card, com a MESMA largura dele
          (var do Popover do Radix) e ~4 px de afastamento. Itens com 44 px de
          altura mínima (alvo de toque/a11y, retorno da QA). */}
      <DropdownMenuContent
        align="start"
        sideOffset={4}
        className="w-[var(--radix-popover-trigger-width)]"
        // AC-03 — o Popover NÃO modal do Radix só devolve o foco ao gatilho
        // quando fecha por Esc/escolha; no clique fora ele deixa o foco na
        // página. Aqui o foco volta ao card em TODO fechamento.
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          cardRef.current?.focus();
        }}
      >
        <DropdownMenuClose asChild>
          <DropdownMenuItem
            className="min-h-11"
            data-testid="align-jobs-share-form"
            onClick={onShareForm}
          >
            Compartilhar formulário
          </DropdownMenuItem>
        </DropdownMenuClose>
        <DropdownMenuClose asChild>
          <DropdownMenuItem
            className="min-h-11"
            data-testid="align-jobs-edit-form"
            onClick={onEditForm}
          >
            Editar formulário
          </DropdownMenuItem>
        </DropdownMenuClose>
        <DropdownMenuClose asChild>
          <DropdownMenuItem className="min-h-11" data-testid="align-jobs-export" onClick={onExport}>
            Exportar alinhamento
          </DropdownMenuItem>
        </DropdownMenuClose>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** EU-654 — labels do dropdown "Tipo de base:" (renomeação RN-01 inclusa). */
const EXPORT_RESPONSES_LABEL: Record<ExportResponsesType, string> = {
  complete: 'Completa',
  'bulk-resumes': 'Currículos em massa',
  'bulk-attestations': 'Laudos em massa',
  'fit-cultural': 'FIT Cultural',
  'learning-map': 'Raio-X Comportamental',
  'career-xray': 'Raio-X de Carreira',
};

/**
 * "Exportar respostas" — dropdown "Tipo de base:" DINÂMICO (EU-654 RN-02/03):
 * renderiza exatamente os tipos que o backend ofertou, na ordem recebida;
 * `types` undefined = disponibilidade carregando (GPR-008 superseded).
 */
function ExportResponsesCard({
  enabled,
  onExport,
  types,
}: {
  enabled: boolean;
  onExport: (type: ExportResponsesType) => void;
  types: ExportResponsesType[] | undefined;
}) {
  if (!enabled) {
    return (
      <ActionCard
        testId="program-management-export-responses"
        label="Exportar respostas"
        Icon={DownloadIcon}
        enabled={false}
        disabledTooltip="Sem candidatos"
        onClick={() => undefined}
      />
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="program-management-export-responses"
          className={cn(
            ACTION_CARD_CLASS,
            'transition-colors hover:border-primary/40 hover:bg-muted/40',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
          )}
        >
          <ActionCardBody label="Exportar respostas" Icon={DownloadIcon} enabled />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>Tipo de base:</DropdownMenuLabel>
        {/* DropdownMenuItem do @eureca/ui é <button> puro (Popover custom):
            handler vai em onClick — onSelect NUNCA dispara em clique. O Close
            fecha o popover na escolha (o download segue com o toast de 3
            estados). EU-654 — itens DINÂMICOS do backend, ordem recebida. */}
        {types === undefined ? (
          <DropdownMenuItem disabled data-testid="export-responses-loading">
            Carregando tipos…
          </DropdownMenuItem>
        ) : (
          types.map((type) => (
            <DropdownMenuClose asChild key={type}>
              <DropdownMenuItem
                data-testid={`export-responses-${type}`}
                onClick={() => onExport(type)}
              >
                {EXPORT_RESPONSES_LABEL[type]}
              </DropdownMenuItem>
            </DropdownMenuClose>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
